import { Button } from '@cian/ui-kit';
import { TButtonTheme } from '@cian/ui-kit/button/types/TButtonTheme';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { trackWriteClick, trackWriteClickBlocked } from './tracking';
import { useEarlyAccessPaywallContext } from '../../../../../../../packages/EarlyAccessPaywall';
import { EAccessCheckTrackingStatus } from '../../../../../../../packages/EarlyAccessPaywall/shared/components/Provider/Provider';
import { EStatus, ESubscriptionLimitType } from '../../../../../../../packages/EarlyAccessPaywall/shared/types/context';
import { useOfferContext } from '../../../../context';
import { selectEarlyAccessContactsFlowEnabled } from 'shared/selectors/earlyAccess/selectEarlyAccessContactsFlowEnabled';

interface IOfferChatButtonContainerProps {
  theme?: TButtonTheme;
}

export const OfferChatButtonContainer: React.FC<IOfferChatButtonContainerProps> = ({ theme = 'fill_secondary' }) => {
  const {
    offer,
    api,
    isChatAvailable,
    position,
    isNewbuildingFromBuilder,
    jsonQuery,
    user: { isAuthenticated },
  } = useOfferContext();
  const chatButtonText = isNewbuildingFromBuilder ? 'Написать застройщику' : 'Написать';

  const earlyAccessContext = useEarlyAccessPaywallContext();
  const checkContactsAccess = earlyAccessContext?.checkContactsAccess;
  const status = earlyAccessContext?.status;
  const isEarlyAccessContactsFlowEnabled =
    // TODO: Удалить фича-тоггл
    useSelector(selectEarlyAccessContactsFlowEnabled) && offer.isEarlyAccessEnabled && earlyAccessContext;

  const handleEarlyAccessFlow = React.useCallback(async () => {
    if (!isAuthenticated) {
      trackWriteClickBlocked({ offer, position, jsonQuery, blockType: EAccessCheckTrackingStatus.Unauthorized });
    }

    const { isConfirmed, trackingStatus } = await checkContactsAccess({
      offerId: offer.id,
      limitType: ESubscriptionLimitType.Chat,
    });

    if (isConfirmed) {
      // TODO: Поправить аналитику когда будут готовы требования
      trackWriteClick({ offer, position, jsonQuery, isEarlyAccess: true });
      api.openChat(offer, position);
    } else {
      trackWriteClickBlocked({ offer, position, jsonQuery, blockType: trackingStatus });
    }
  }, [offer, position, jsonQuery, api, checkContactsAccess, isAuthenticated]);

  const handleClick = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (isEarlyAccessContactsFlowEnabled) {
        await handleEarlyAccessFlow();

        return;
      }

      trackWriteClick({ offer, position, jsonQuery, isEarlyAccess: false });
      api.openChat(offer, position);
    },
    [isEarlyAccessContactsFlowEnabled, offer, position, jsonQuery, api, handleEarlyAccessFlow],
  );

  if (!isChatAvailable) {
    return null;
  }

  return (
    <Button
      data-mark="ChatButton"
      fullWidth
      size="XS"
      onClick={handleClick}
      theme={theme}
      loading={status === EStatus.Loading}
    >
      {chatButtonText}
    </Button>
  );
};
